import type { DriveStep, Side } from 'driver.js'

export interface TourStep {
  element: string
  title: string
  content: string
  /** Posición del popover respecto al elemento en Driver.js v1. */
  position?: Side
}

export type TourId = 'orders' | 'products' | 'inventory-sync' | 'escalation'

export interface TourDefinition {
  id: TourId
  /** Ruta donde aplica el tour (para navegación y disparo automático). */
  path: string
  title: string
  /**
   * Solo la guía del panel se abría sola; al retirarse del dashboard,
   * el resto se lanza únicamente desde el botón "Guía" del header.
   */
  autoStart?: boolean
  steps: TourStep[]
}

/** Guía contextual de la bandeja de órdenes. */
const ordersTour: TourDefinition = {
  id: 'orders',
  path: '/app/orders',
  title: 'Órdenes y ventas',
  steps: [
    {
      element: '[data-tour="orders-main"]',
      title: 'Las ventas que nacen en el chat',
      content:
        'Cada compra confirmada por WhatsApp se registra aquí con sus productos, su total y su forma de entrega. No hay que capturarla a mano: la orden viene de la conversación.',
      position: 'bottom',
    },
    {
      element: '[data-tour="orders-actions"]',
      title: 'Crear a mano o exportar',
      content:
        'Usa "Nueva orden" si la venta fue presencial o por teléfono, y "Exportar a Excel" para cuadrar caja o pasar el listado a contabilidad.',
      position: 'bottom',
    },
    {
      element: '[data-tour="orders-filters"]',
      title: 'Trabaja por tandas',
      content:
        'Filtra por estado para ir directo a lo que toca hoy: pendientes de verificar pago, confirmadas para despacho o completadas. También puedes buscar por cliente, teléfono o número de orden.',
      position: 'bottom',
    },
    {
      element: '[data-tour="orders-list"]',
      title: 'Qué hacer con cada orden',
      content:
        'Al abrir una orden ves el detalle completo y cambias su estado conforme avanza el pago o la entrega. Todo queda ligado al cliente y a la conversación donde se cerró la venta.',
      position: 'right',
    },
  ],
}

/** Guía contextual del panel de productos. */
const productsTour: TourDefinition = {
  id: 'products',
  path: '/app/products',
  title: 'Gestión de productos',
  steps: [
    {
      element: '[data-tour="products-main"]',
      title: 'Lo que tu bot puede vender',
      content:
        'Aquí administras nombre, precio, disponibilidad y categoría de cada producto. Es lo que alimenta las recomendaciones del asistente y la vitrina pública.',
      position: 'bottom',
    },
    {
      element: '[data-tour="products-search"]',
      title: 'Encuentra el producto rápido',
      content:
        'Busca por nombre, SKU o categoría, y usa las etiquetas para trabajar solo un grupo. El contador te dice cuántos productos estás viendo del total.',
      position: 'bottom',
    },
    {
      element: '[data-tour="products-table"]',
      title: 'Edita como en una hoja de cálculo',
      content:
        'Cambia precio, disponibilidad o categoría directo en la fila. Verás en verde lo nuevo y en ámbar lo modificado sin guardar; revisa y pulsa "Guardar cambios".',
      position: 'top',
    },
    {
      element: '[data-tour="products-actions"]',
      title: 'Catálogo público e importación',
      content:
        'Desde estos botones abres o copias el enlace de tu catálogo web, descargas la plantilla, importas tu Excel y refrescas el listado cuando cambies algo por fuera.',
      position: 'bottom',
    },
  ],
}

/** Guía contextual de la sincronización de inventario. */
const inventorySyncTour: TourDefinition = {
  id: 'inventory-sync',
  path: '/app/inventory-sync',
  title: 'Sincronización de inventario',
  steps: [
    {
      element: '[data-tour="inventory-overview"]',
      title: 'Cómo está tu inventario hoy',
      content:
        'El resumen te dice cuántos productos tienes, cuántos están disponibles, cuántos están en stock crítico y cuándo fue la última sincronización con tu fuente de verdad.',
      position: 'bottom',
    },
    {
      element: '[data-tour="inventory-sheets"]',
      title: 'Conecta tu Google Sheet',
      content:
        'Pega el enlace de tu hoja, prueba la conexión y sincroniza precios y existencias sin volver a cargarlos a mano. Desde ese momento la hoja manda sobre el catálogo.',
      position: 'bottom',
    },
    {
      element: '[data-tour="inventory-jit"]',
      title: 'Verificación antes de cerrar la venta',
      content:
        'Con el modo estricto activo, el bot confirma existencias en el momento de la compra. Si la fuente no responde, pide validación con un asesor en lugar de vender algo agotado.',
      position: 'bottom',
    },
    {
      element: '[data-tour="inventory-outbound"]',
      title: 'Descuento y reposición automática',
      content:
        'Si activas las notificaciones salientes, cada venta o cancelación descuenta o repone unidades en tu hoja o ERP, y tu stock físico y el del bot dejan de separarse.',
      position: 'bottom',
    },
  ],
}

/** Guía contextual de la derivación a atención humana. */
const escalationTour: TourDefinition = {
  id: 'escalation',
  path: '/app/bot-settings/escalation',
  title: 'Derivación a atención humana',
  steps: [
    {
      element: '[data-tour="escalation-main"]',
      title: 'Cuándo el bot pasa el testigo',
      content:
        'Aquí defines cómo tu asistente deriva a una persona: en qué momento lo hace, qué mensaje envía y a quién avisa. La conversación no se pierde, solo cambia de manos.',
      position: 'bottom',
    },
    {
      element: '[data-tour="escalation-presets"]',
      title: 'Elige el tono del traspaso',
      content:
        'Aplica un estilo ya escrito (directo, cálido o formal) y ajusta el cargo de quien recibe el chat. El cliente siempre sabe con quién va a hablar.',
      position: 'bottom',
    },
    {
      element: '[data-tour="escalation-rules"]',
      title: 'Mensajes y límites',
      content:
        'Revisa el mensaje de derivación y abre las opciones avanzadas para cambiar el límite de respuestas antes de transferir. Guarda al final para que el bot use el texto nuevo.',
      position: 'top',
    },
  ],
}

export const TOURS: TourDefinition[] = [
  ordersTour,
  productsTour,
  inventorySyncTour,
  escalationTour,
]

export const TOUR_IDS: TourId[] = TOURS.map((t) => t.id)

export function getTourById(id: TourId): TourDefinition | undefined {
  return TOURS.find((t) => t.id === id)
}

/**
 * Resuelve el tour que corresponde a la ruta actual.
 * Durante `/app/onboarding` retorna `undefined` para que la guía interactiva
 * nunca interrumpa el asistente de configuración inicial.
 */
export function getTourForPath(pathname: string): TourDefinition | undefined {
  if (!pathname || pathname.startsWith('/app/onboarding') || pathname.startsWith('/onboarding')) {
    return undefined
  }
  return TOURS.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`))
}

interface AutoStartContext {
  tour: TourDefinition | undefined
  /** El tenant ya terminó o cerró esta guía en una visita anterior. */
  hasSeen: boolean
  /** El cliente pulsó "Omitir guía": no se vuelve a abrir sola nunca. */
  autoSuppressed: boolean
}

/**
 * Única regla de arranque automático: la guía del panel, la primera vez.
 * Cualquier otro módulo se abre solo cuando el cliente pulsa el botón "Guía".
 */
export function shouldAutoStartTour({ tour, hasSeen, autoSuppressed }: AutoStartContext): boolean {
  if (!tour?.autoStart) return false
  return !hasSeen && !autoSuppressed
}

/**
 * Convierte los pasos del catálogo al contrato `DriveStep` de Driver.js v1.
 */
export function mapToDriveSteps(steps: TourStep[]): DriveStep[] {
  return steps.map((s) => ({
    element: s.element,
    popover: {
      title: s.title,
      description: s.content,
      side: s.position,
    },
  }))
}
